import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Textarea,
  VStack,
  keyframes,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  LuBarChart3,
  LuBrain,
  LuCornerDownRight,
  LuFileText,
  LuMessageCircle,
  LuPlus,
  LuSearch,
  LuSend,
  LuSparkles,
  LuTarget,
  LuTrendingUp,
  LuWrench,
  LuZap,
} from 'react-icons/lu';
import ReactMarkdown from 'react-markdown';
import {
  getChatStatus,
  getConversation,
  listConversations,
  sendChatMessage,
} from '../services/chatService';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const TOOL_LABELS = {
  get_businesses: 'Queried businesses',
  get_monthly_trends: 'Checked monthly trends',
  get_revenue_estimates: 'Fetched revenue estimates',
  get_market_overview: 'Loaded market overview',
  get_competitive_intel: 'Looked up competitive intel',
  save_business_insight: 'Saved insight',
  save_report: 'Saved report',
  search_reviews: 'Searched reviews',
};

const FOLLOW_UP_SUGGESTIONS = {
  get_businesses: [
    'Which of these businesses has the most reviews?',
    'Show me the lowest-rated businesses and why',
    'What common themes appear in their reviews?',
    'Compare the top 3 businesses side by side',
  ],
  get_monthly_trends: [
    'Which business improved the most recently?',
    'Are there any seasonal patterns in the data?',
    'Who is losing momentum and why?',
    'Predict which businesses will grow next month',
  ],
  get_revenue_estimates: [
    'Which business has the best review-to-revenue ratio?',
    'Are there underperformers with hidden potential?',
    'How does rating correlate with revenue here?',
    'Who is the most efficient competitor?',
  ],
  get_market_overview: [
    'What gaps exist that no business is filling?',
    'Which positive themes should I focus on?',
    'What are customers complaining about most?',
    'How does this market compare to industry standards?',
  ],
  get_competitive_intel: [
    'How can I outrank this competitor online?',
    'What keywords are they missing?',
    'Compare their web presence to the market leader',
    'What advertising strategies are working for them?',
  ],
  search_reviews: [
    'Find the opposite sentiment in reviews',
    'Which businesses handle this issue best?',
    'Are there patterns in when these reviews were posted?',
    'What do 5-star reviews say about this topic?',
  ],
  default: [
    'Show me the top performers in this market',
    'What are customers complaining about most?',
    'Find businesses with great reviews but few customers',
    'Generate a competitive analysis report',
  ],
};

const SUGGESTED_PROMPTS = [
  {
    icon: LuSearch,
    title: 'Deep Review Mining',
    prompt:
      'Find all reviews mentioning "pricing" or "expensive" and tell me which businesses handle price objections best vs worst',
    gradient: 'linear(to-br, purple.400, pink.400)',
  },
  {
    icon: LuTrendingUp,
    title: 'Trend Prediction',
    prompt:
      'Analyze the last 6 months of rating trends and predict which 3 businesses are most likely to overtake market leaders',
    gradient: 'linear(to-br, blue.400, cyan.400)',
  },
  {
    icon: LuBarChart3,
    title: 'Competitive Battle',
    prompt:
      'Compare the top 5 businesses by competitive intelligence - who has the strongest online presence and weakest?',
    gradient: 'linear(to-br, orange.400, yellow.400)',
  },
  {
    icon: LuTarget,
    title: 'Market Gaps',
    prompt:
      'What recurring complaints appear in 1-star reviews that no business in this market is addressing well?',
    gradient: 'linear(to-br, green.400, teal.400)',
  },
  {
    icon: LuFileText,
    title: 'Executive Report',
    prompt:
      'Generate an executive summary report of market health including top performers, emerging threats, and customer sentiment shifts',
    gradient: 'linear(to-br, red.400, pink.400)',
  },
  {
    icon: LuZap,
    title: 'Revenue Intelligence',
    prompt:
      'Which businesses have the highest review-to-revenue correlation? Are there underperformers with hidden potential?',
    gradient: 'linear(to-br, indigo.400, purple.400)',
  },
  {
    icon: LuBrain,
    title: 'Sentiment Patterns',
    prompt:
      'Search reviews for emotional language patterns - what makes customers "love" vs just "like" businesses here?',
    gradient: 'linear(to-br, cyan.400, blue.400)',
  },
  {
    icon: LuSparkles,
    title: 'Hidden Gems',
    prompt:
      "Find businesses with <50 reviews but exceptional ratings - who are the rising stars the market doesn't know about yet?",
    gradient: 'linear(to-br, pink.400, purple.400)',
  },
];

function getFollowUpSuggestions(toolsCalled = []) {
  const suggestions = new Set();

  toolsCalled.forEach((toolCall) => {
    const toolSuggestions = FOLLOW_UP_SUGGESTIONS[toolCall.tool];
    if (toolSuggestions) {
      const shuffled = [...toolSuggestions].sort(() => 0.5 - Math.random());
      shuffled.slice(0, 2).forEach((suggestion) => suggestions.add(suggestion));
    }
  });

  if (suggestions.size < 2) {
    const defaults = [...FOLLOW_UP_SUGGESTIONS.default].sort(
      () => 0.5 - Math.random()
    );
    defaults.forEach((suggestion) => {
      if (suggestions.size < 2) suggestions.add(suggestion);
    });
  }

  return Array.from(suggestions).slice(0, 2);
}

function formatConversationDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function SuggestedPromptCard({ icon, title, prompt, gradient, onClick }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const titleColor = useColorModeValue('gray.800', 'gray.100');
  const bodyColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      as="button"
      onClick={onClick}
      p={{ base: 3.5, md: 4 }}
      borderRadius="2xl"
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      textAlign="left"
      transition="all 0.2s"
      h="100%"
      _hover={{
        transform: 'translateY(-2px)',
        shadow: 'lg',
        borderColor: 'blue.200',
      }}
      _active={{
        transform: 'translateY(0)',
      }}
    >
      <VStack align="start" spacing={3}>
        <Flex
          w="36px"
          h="36px"
          borderRadius="xl"
          bgGradient={gradient}
          align="center"
          justify="center"
          shadow="sm"
        >
          <Icon as={icon} color="white" boxSize={4.5} />
        </Flex>
        <Box>
          <Text fontWeight="600" fontSize="sm" color={titleColor} mb={1}>
            {title}
          </Text>
          <Text fontSize="xs" color={bodyColor} noOfLines={2} lineHeight="1.5">
            {prompt}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
}

function FollowUpSuggestion({ text, onClick }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('blue.100', 'gray.600');
  const hoverBg = useColorModeValue('blue.50', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.100');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const iconColor = useColorModeValue('blue.500', 'blue.200');

  return (
    <Box
      as="button"
      onClick={onClick}
      p={3}
      borderRadius="xl"
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      textAlign="left"
      transition="all 0.2s"
      _hover={{
        bg: hoverBg,
        borderColor: 'blue.300',
        transform: 'translateX(4px)',
        shadow: 'sm',
      }}
      _active={{
        transform: 'translateX(2px)',
      }}
    >
      <HStack spacing={3} align="flex-start">
        <Flex
          w="24px"
          h="24px"
          borderRadius="md"
          bg={iconBg}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={LuCornerDownRight} color={iconColor} boxSize={3.5} />
        </Flex>
        <Text fontSize="sm" color={textColor} fontWeight="500" lineHeight="1.5">
          {text}
        </Text>
      </HStack>
    </Box>
  );
}

function AssistantMessage({ content, toolsCalled, onFollowUpClick, isLastMessage }) {
  const toolBg = useColorModeValue('blue.50', 'gray.700');
  const toolColor = useColorModeValue('blue.700', 'blue.200');
  const toolBorder = useColorModeValue('blue.100', 'gray.600');
  const markdownColor = useColorModeValue('gray.700', 'gray.200');
  const strongColor = useColorModeValue('gray.800', 'white');
  const codeBg = useColorModeValue('gray.100', 'gray.700');
  const preBg = useColorModeValue('gray.50', 'gray.700');
  const preBorder = useColorModeValue('gray.200', 'gray.600');
  const tableBg = useColorModeValue('gray.50', 'gray.700');
  const followupLabelColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box>
      {toolsCalled && toolsCalled.length > 0 && (
        <HStack spacing={1.5} mb={3} flexWrap="wrap">
          <Flex
            w="18px"
            h="18px"
            borderRadius="md"
            bg={toolBg}
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Box as={LuWrench} size="10px" color={toolColor} />
          </Flex>
          {toolsCalled.map((toolCall, index) => (
            <Tag
              key={`${toolCall.tool}-${index}`}
              size="sm"
              bg={toolBg}
              borderRadius="full"
              border="1px solid"
              borderColor={toolBorder}
              px={2.5}
              py={1}
            >
              <TagLabel fontSize="xs" color={toolColor} fontWeight="500">
                {TOOL_LABELS[toolCall.tool] || toolCall.tool}
              </TagLabel>
            </Tag>
          ))}
        </HStack>
      )}
      <Box
        fontSize="sm"
        lineHeight="1.7"
        color={markdownColor}
        sx={{
          '& p': { mb: 3 },
          '& p:last-child': { mb: 0 },
          '& ul, & ol': { pl: 5, mb: 3 },
          '& li': { mb: 1.5 },
          '& h1': { fontSize: 'lg', fontWeight: 'bold', mb: 2, mt: 4 },
          '& h2': { fontSize: 'md', fontWeight: 'bold', mb: 2, mt: 4 },
          '& h3': { fontSize: 'sm', fontWeight: 'bold', mb: 1.5, mt: 3 },
          '& strong': { fontWeight: '600', color: strongColor },
          '& code': {
            bg: codeBg,
            px: 1.5,
            py: 0.5,
            borderRadius: 'md',
            fontSize: 'xs',
          },
          '& pre': {
            bg: preBg,
            p: 3,
            borderRadius: 'lg',
            overflowX: 'auto',
            mb: 3,
            border: '1px solid',
            borderColor: preBorder,
          },
          '& table': { width: '100%', mb: 3 },
          '& th, & td': {
            border: '1px solid',
            borderColor: preBorder,
            px: 3,
            py: 2,
            fontSize: 'xs',
          },
          '& th': { fontWeight: '600', bg: tableBg },
          '& a': {
            color: 'blue.500',
            textDecoration: 'underline',
            fontWeight: '500',
          },
        }}
      >
        <ReactMarkdown>{content}</ReactMarkdown>
      </Box>

      {isLastMessage && (
        <VStack spacing={2} mt={5} align="stretch">
          <Text
            fontSize="xs"
            color={followupLabelColor}
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            Continue exploring
          </Text>
          {getFollowUpSuggestions(toolsCalled).map((suggestion, index) => (
            <FollowUpSuggestion
              key={`${suggestion}-${index}`}
              text={suggestion}
              onClick={() => onFollowUpClick(suggestion)}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}

export default function ChatPage() {
  const { brandName } = useUser();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [streamingTools, setStreamingTools] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);

  const shellBg = useColorModeValue('white', 'gray.800');
  const shellBorder = useColorModeValue('gray.200', 'gray.700');
  const shellShadow = useColorModeValue(
    '0 16px 36px -28px rgba(14, 31, 53, 0.45)',
    'none'
  );
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const historyBg = useColorModeValue('gray.50', 'gray.900');
  const historyHover = useColorModeValue('gray.100', 'gray.700');
  const historyActive = useColorModeValue('blue.50', 'blue.900');
  const historyActiveBorder = useColorModeValue('blue.200', 'blue.600');
  const historyActiveText = useColorModeValue('blue.700', 'blue.100');
  const historyText = useColorModeValue('gray.700', 'gray.100');
  const canvasBg = useColorModeValue('gray.50', 'gray.800');
  const userBubbleBg = useColorModeValue(
    'linear(to-br, blue.500, blue.600)',
    'linear(to-br, blue.500, blue.400)'
  );
  const assistantBubbleBg = useColorModeValue('white', 'gray.700');
  const assistantBubbleBorder = useColorModeValue('gray.200', 'gray.600');
  const assistantIconBg = useColorModeValue('blue.50', 'blue.900');
  const assistantIconColor = useColorModeValue('blue.600', 'blue.200');
  const composerBg = useColorModeValue('gray.50', 'gray.700');
  const composerBorder = useColorModeValue('gray.200', 'gray.600');
  const composerText = useColorModeValue('gray.700', 'gray.100');
  const helperText = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const loadConversations = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const data = await listConversations();
      if (data.success) {
        setConversations(data.conversations || []);
      }
    } catch (error) {
      logger.error('Failed to load conversations:', error);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingTools, scrollToBottom]);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadConversations();
    return stopPolling;
  }, [loadConversations, stopPolling]);

  const openConversation = useCallback(
    async (convId) => {
      setIsLoading(true);
      try {
        const data = await getConversation(convId);
        if (data.success) {
          setMessages(data.conversation?.messages || []);
          setConversationId(convId);
        }
      } catch (error) {
        logger.error('Failed to load conversation:', error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const startNewChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setInputValue('');
    setStreamingTools([]);
    stopPolling();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [stopPolling]);

  const finishAssistantResponse = useCallback(
    (content, toolsCalled = []) => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content,
          timestamp: new Date().toISOString(),
          tools_called: toolsCalled,
        },
      ]);
      setStreamingTools([]);
      setIsLoading(false);
    },
    []
  );

  const handleSend = useCallback(
    async (customMessage = null) => {
      const trimmed = (customMessage || inputValue).trim();
      if (!trimmed || isLoading) return;

      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: trimmed,
          timestamp: new Date().toISOString(),
        },
      ]);
      setInputValue('');
      setIsLoading(true);
      setStreamingTools([]);
      stopPolling();

      try {
        const data = await sendChatMessage(trimmed, conversationId);
        if (!data.success) throw new Error('Failed to send message');

        const activeConversationId = data.conversation_id;
        if (!conversationId) {
          setConversationId(activeConversationId);
        }

        pollingRef.current = setInterval(async () => {
          try {
            const status = await getChatStatus(activeConversationId);
            if (status.tools_called && status.tools_called.length > 0) {
              setStreamingTools(status.tools_called);
            }

            if (status.status === 'done') {
              stopPolling();
              finishAssistantResponse(status.reply, status.tools_called || []);
              loadConversations();
            } else if (status.status === 'error') {
              stopPolling();
              finishAssistantResponse(
                status.error || 'Sorry, something went wrong. Please try again.'
              );
            }
          } catch (pollError) {
            logger.error('Polling error:', pollError);
            stopPolling();
            finishAssistantResponse(
              'Sorry, something went wrong while processing this request. Please try again.'
            );
          }
        }, 1000);
      } catch (error) {
        logger.error('Failed to send message:', error);
        finishAssistantResponse(
          'Sorry, something went wrong while sending your message. Please try again.'
        );
      }
    },
    [
      conversationId,
      finishAssistantResponse,
      inputValue,
      isLoading,
      loadConversations,
      stopPolling,
    ]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handlePromptClick = useCallback(
    (prompt) => {
      handleSend(prompt);
    },
    [handleSend]
  );

  const toggleHistory = useCallback(() => {
    if (!showHistory) {
      loadConversations();
    }
    setShowHistory((prev) => !prev);
  }, [loadConversations, showHistory]);

  const renderEmptyState = () => (
    <VStack spacing={6} align="stretch" py={{ base: 2, md: 4 }}>
      <Flex direction="column" align="center" textAlign="center" px={2}>
        <Flex
          w={{ base: '62px', md: '70px' }}
          h={{ base: '62px', md: '70px' }}
          borderRadius="2xl"
          bgGradient="linear(to-br, blue.400, purple.500)"
          align="center"
          justify="center"
          mb={4}
          shadow="lg"
          animation={`${float} 3s ease-in-out infinite`}
        >
          <Icon as={LuSparkles} color="white" boxSize={{ base: 6, md: 7 }} />
        </Flex>
        <Text
          fontSize={{ base: 'xl', md: '2xl' }}
          fontWeight="700"
          color={titleColor}
          mb={2}
        >
          Ask anything about your market
        </Text>
        <Text
          fontSize={{ base: 'sm', md: 'md' }}
          color={subtitleColor}
          maxW="740px"
          lineHeight="1.7"
        >
          Use {brandName} AI to search reviews, identify trend shifts, compare
          competitors, and generate focused strategic recommendations.
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        {SUGGESTED_PROMPTS.map((prompt, index) => (
          <SuggestedPromptCard
            key={`${prompt.title}-${index}`}
            icon={prompt.icon}
            title={prompt.title}
            prompt={prompt.prompt}
            gradient={prompt.gradient}
            onClick={() => handlePromptClick(prompt.prompt)}
          />
        ))}
      </SimpleGrid>

      <Flex
        gap={{ base: 2, md: 4 }}
        wrap="wrap"
        justify="center"
        border="1px dashed"
        borderColor={shellBorder}
        borderRadius="xl"
        p={3}
        bg={shellBg}
      >
        {[
          'Searches review text',
          'Tracks momentum',
          'Compares competitors',
          'Generates executive summaries',
        ].map((item) => (
          <Badge
            key={item}
            colorScheme="green"
            variant="subtle"
            px={2.5}
            py={1}
            borderRadius="full"
            fontWeight="500"
          >
            {item}
          </Badge>
        ))}
      </Flex>
    </VStack>
  );

  const renderMessages = () => (
    <VStack spacing={5} align="stretch" py={2}>
      {messages.map((message, index) => {
        const isUser = message.role === 'user';
        const isLastAssistantMessage =
          message.role === 'assistant' &&
          (index === messages.length - 1 ||
            (index === messages.length - 2 &&
              messages[messages.length - 1]?.role === 'user'));

        return (
          <Flex
            key={`${message.timestamp || index}-${index}`}
            justify={isUser ? 'flex-end' : 'flex-start'}
            gap={2.5}
          >
            {!isUser && (
              <Flex
                w="30px"
                h="30px"
                borderRadius="xl"
                align="center"
                justify="center"
                bg={assistantIconBg}
                color={assistantIconColor}
                border="1px solid"
                borderColor={assistantBubbleBorder}
                flexShrink={0}
                mt="2px"
              >
                <Icon as={LuSparkles} boxSize={3.5} />
              </Flex>
            )}

            <Box
              maxW={{
                base: isUser ? '92%' : '100%',
                md: isUser ? '74%' : '88%',
              }}
              px={{ base: 4, md: 5 }}
              py={{ base: 3, md: 4 }}
              borderRadius="2xl"
              borderBottomRightRadius={isUser ? 'md' : '2xl'}
              borderBottomLeftRadius={isUser ? '2xl' : 'md'}
              bg={isUser ? undefined : assistantBubbleBg}
              bgGradient={isUser ? userBubbleBg : undefined}
              color={isUser ? 'white' : undefined}
              border={isUser ? 'none' : '1px solid'}
              borderColor={isUser ? undefined : assistantBubbleBorder}
              shadow={isUser ? 'md' : 'sm'}
            >
              {isUser ? (
                <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.65">
                  {message.content}
                </Text>
              ) : (
                <AssistantMessage
                  content={message.content}
                  toolsCalled={message.tools_called}
                  onFollowUpClick={handlePromptClick}
                  isLastMessage={isLastAssistantMessage && !isLoading}
                />
              )}
            </Box>
          </Flex>
        );
      })}

      {isLoading && (
        <Flex justify="flex-start" gap={2.5}>
          <Flex
            w="30px"
            h="30px"
            borderRadius="xl"
            align="center"
            justify="center"
            bg={assistantIconBg}
            color={assistantIconColor}
            border="1px solid"
            borderColor={assistantBubbleBorder}
            flexShrink={0}
            mt="2px"
          >
            <Icon as={LuSparkles} boxSize={3.5} />
          </Flex>
          <Box
            maxW={{ base: '100%', md: '88%' }}
            px={5}
            py={4}
            borderRadius="2xl"
            borderBottomLeftRadius="md"
            bg={assistantBubbleBg}
            border="1px solid"
            borderColor={assistantBubbleBorder}
          >
            {streamingTools.length > 0 && (
              <HStack spacing={1.5} mb={3} flexWrap="wrap">
                <Flex
                  w="18px"
                  h="18px"
                  borderRadius="md"
                  bg={assistantIconBg}
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Box as={LuWrench} size="10px" color={assistantIconColor} />
                </Flex>
                {streamingTools.map((toolCall, index) => (
                  <Tag
                    key={`${toolCall.tool}-${index}`}
                    size="sm"
                    bg={assistantIconBg}
                    borderRadius="full"
                    border="1px solid"
                    borderColor={assistantBubbleBorder}
                    px={2.5}
                  >
                    <TagLabel fontSize="xs" color={assistantIconColor} fontWeight="500">
                      {TOOL_LABELS[toolCall.tool] || toolCall.tool}
                    </TagLabel>
                  </Tag>
                ))}
              </HStack>
            )}
            <HStack spacing={3}>
              <Flex
                w="24px"
                h="24px"
                borderRadius="md"
                bgGradient="linear(to-br, blue.400, purple.500)"
                align="center"
                justify="center"
              >
                <Spinner size="xs" color="white" thickness="2px" />
              </Flex>
              <Box>
                <Text fontSize="sm" color={composerText} fontWeight="500">
                  {streamingTools.length > 0 ? 'Analyzing your data...' : 'Thinking...'}
                </Text>
                {streamingTools.length > 0 && (
                  <Text fontSize="xs" color={helperText} mt={0.5}>
                    Running {streamingTools.length} tool
                    {streamingTools.length > 1 ? 's' : ''}
                  </Text>
                )}
              </Box>
            </HStack>
          </Box>
        </Flex>
      )}

      <div ref={messagesEndRef} />
    </VStack>
  );

  const chatPanelHiddenOnMobile = showHistory ? { base: 'none', lg: 'flex' } : 'flex';
  const historyPanelDisplay = showHistory ? 'block' : 'none';

  return (
    <Box
      bg={shellBg}
      borderRadius={{ base: 'xl', md: '2xl' }}
      boxShadow={shellShadow}
      borderWidth="1px"
      borderColor={shellBorder}
      h={{ base: 'calc(100vh - 146px)', md: 'calc(100vh - 116px)' }}
      minH={{ base: '480px', md: '560px' }}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 4.5 }} bg={headerBg} borderBottom="1px solid" borderColor={shellBorder}>
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} wrap="wrap">
          <HStack spacing={3.5}>
            <Flex
              w={{ base: '44px', md: '48px' }}
              h={{ base: '44px', md: '48px' }}
              borderRadius="xl"
              bgGradient="linear(to-br, blue.400, purple.500)"
              align="center"
              justify="center"
              animation={`${float} 3s ease-in-out infinite`}
              shadow="md"
              flexShrink={0}
            >
              <Icon as={LuSparkles} color="white" boxSize={{ base: 5, md: 6 }} />
            </Flex>
            <Box>
              <HStack spacing={2}>
                <Text fontWeight="700" fontSize={{ base: 'lg', md: 'xl' }} color={titleColor}>
                  Chat Intelligence Studio
                </Text>
                <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={2}>
                  New
                </Badge>
              </HStack>
              <Text fontSize="sm" color={subtitleColor}>
                Clean workspace for analysis, competitive intel, and reporting
              </Text>
            </Box>
          </HStack>

          <HStack spacing={2}>
            <Button
              leftIcon={<LuMessageCircle />}
              variant={showHistory ? 'solid' : 'outline'}
              colorScheme={showHistory ? 'blue' : 'gray'}
              size="sm"
              borderRadius="lg"
              onClick={toggleHistory}
            >
              {showHistory ? 'Hide history' : 'Show history'}
            </Button>
            <IconButton
              icon={<LuPlus />}
              variant="outline"
              size="sm"
              borderRadius="lg"
              onClick={startNewChat}
              aria-label="Start new chat"
            />
          </HStack>
        </Flex>
      </Box>

      <Flex flex="1" minH={0}>
        <Box
          display={historyPanelDisplay}
          w={{ base: '100%', lg: '300px' }}
          flexShrink={0}
          borderRight={{ base: 'none', lg: '1px solid' }}
          borderBottom={{ base: '1px solid', lg: 'none' }}
          borderColor={shellBorder}
          bg={historyBg}
          overflowY="auto"
          p={4}
        >
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="600" fontSize="sm" color={subtitleColor} textTransform="uppercase" letterSpacing="wide">
              Conversations
            </Text>
            <Badge borderRadius="full" px={2}>
              {conversations.length}
            </Badge>
          </HStack>

          {isLoadingList ? (
            <Flex justify="center" py={8}>
              <Spinner size="sm" color="blue.400" />
            </Flex>
          ) : conversations.length === 0 ? (
            <Flex direction="column" align="center" py={8} color={helperText}>
              <Box as={LuMessageCircle} boxSize="24px" mb={2} opacity={0.5} />
              <Text fontSize="xs" textAlign="center">
                No conversations yet
              </Text>
            </Flex>
          ) : (
            <VStack spacing={2} align="stretch">
              {conversations.map((conversation) => {
                const isActive = conversationId === conversation.conversation_id;
                return (
                  <Box
                    key={conversation.conversation_id}
                    px={3}
                    py={3}
                    borderRadius="xl"
                    cursor="pointer"
                    bg={isActive ? historyActive : 'transparent'}
                    _hover={{ bg: isActive ? historyActive : historyHover }}
                    onClick={() => openConversation(conversation.conversation_id)}
                    transition="all 0.15s"
                    border="1px solid"
                    borderColor={isActive ? historyActiveBorder : 'transparent'}
                  >
                    <Text
                      fontWeight={isActive ? '600' : '500'}
                      fontSize="sm"
                      color={isActive ? historyActiveText : historyText}
                      noOfLines={1}
                    >
                      {conversation.title}
                    </Text>
                    <Text fontSize="xs" color={helperText} mt={1}>
                      {formatConversationDate(conversation.updated_at)}
                    </Text>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>

        <Box
          flex="1"
          minW={0}
          position="relative"
          display={chatPanelHiddenOnMobile}
          bg={canvasBg}
        >
          <Flex
            direction="column"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
          >
          <Box
            flex="1"
            overflowY="auto"
            px={{ base: 4, md: 6, xl: 8 }}
            py={{ base: 4, md: 5 }}
          >
            <Box maxW="1050px" mx="auto" w="100%">
              {messages.length === 0 && !isLoading ? renderEmptyState() : renderMessages()}
            </Box>
          </Box>

          <Box
            flexShrink={0}
            px={{ base: 4, md: 6, xl: 8 }}
            py={{ base: 3, md: 4 }}
            borderTop="1px solid"
            borderColor={shellBorder}
            bg={shellBg}
          >
            <Box maxW="1050px" mx="auto">
              <Flex
                gap={3}
                align="flex-end"
                bg={composerBg}
                borderRadius="2xl"
                p={3}
                border="1px solid"
                borderColor={composerBorder}
                transition="all 0.2s"
                _focusWithin={{
                  borderColor: 'blue.400',
                  bg: shellBg,
                }}
              >
                <Textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about your market data..."
                  isDisabled={isLoading}
                  resize="none"
                  minH="44px"
                  maxH="140px"
                  border="none"
                  p={2}
                  fontSize="md"
                  color={composerText}
                  _placeholder={{ color: helperText }}
                  _focus={{ boxShadow: 'none' }}
                />
                <IconButton
                  icon={<LuSend />}
                  bgGradient={
                    inputValue.trim()
                      ? 'linear(to-br, blue.500, purple.500)'
                      : undefined
                  }
                  bg={inputValue.trim() ? undefined : 'gray.300'}
                  color={inputValue.trim() ? 'white' : 'gray.500'}
                  borderRadius="xl"
                  size="lg"
                  onClick={() => handleSend()}
                  isDisabled={!inputValue.trim() || isLoading}
                  aria-label="Send message"
                  _hover={{
                    bgGradient: inputValue.trim()
                      ? 'linear(to-br, blue.600, purple.600)'
                      : undefined,
                    bg: inputValue.trim() ? undefined : 'gray.400',
                  }}
                />
              </Flex>

              <HStack justify="space-between" mt={2} px={1}>
                <Text fontSize="xs" color={helperText}>
                  Press Enter to send • Shift + Enter for a new line
                </Text>
                {isLoading && (
                  <HStack spacing={1.5}>
                    <Spinner size="xs" color="blue.400" />
                    <Text fontSize="xs" color={helperText}>
                      Generating answer
                    </Text>
                  </HStack>
                )}
              </HStack>
            </Box>
          </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
